
import React from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { 
  Book, 
  Users, 
  Calendar, 
  Clock, 
  GraduationCap, 
  DollarSign, 
  Award, 
  Tag, 
  Heart, 
  Share
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface TrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (data: TrainingProgramData) => void;
}

export interface TrainingProgramData {
  title: string;
  description: string;
  category: string;
  format: string;
  price: string;
  duration: string;
  level: string;
  maxParticipants: string;
  isRemote: boolean;
  isCertified: boolean;
}

export const TrainingModal: React.FC<TrainingModalProps> = ({ 
  isOpen, 
  onClose,
  onCreate
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = React.useState<TrainingProgramData>({
    title: "",
    description: "",
    category: "",
    format: "",
    price: "",
    duration: "",
    level: "",
    maxParticipants: "",
    isRemote: true,
    isCertified: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.category) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (onCreate) {
      onCreate(formData);
    }
    
    toast({
      title: "Training program created",
      description: "Your training program has been published successfully.",
      variant: "success"
    });
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            Create Training Program
          </DialogTitle>
          <DialogDescription>
            Offer your expertise as a training program for individuals or organizations.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-right">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Advanced React Development"
                className="mt-1"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="description" className="text-right">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe what participants will learn from your training program..."
                className="mt-1"
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category" className="text-right">
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.category}
                  onValueChange={(value) => handleSelectChange("category", value)}
                >
                  <SelectTrigger id="category" className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="design">Design</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="productivity">Productivity</SelectItem>
                    <SelectItem value="leadership">Leadership</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="format" className="text-right">
                  Format
                </Label>
                <Select 
                  value={formData.format}
                  onValueChange={(value) => handleSelectChange("format", value)}
                >
                  <SelectTrigger id="format" className="mt-1">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="bootcamp">Bootcamp</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="mentoring">1:1 Mentoring</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" className="text-right">
                  Price ($)
                </Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="e.g., 499"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="duration" className="text-right">
                  Duration
                </Label>
                <Input
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="e.g., 4 weeks, 10 hours"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="level" className="text-right">
                  Level
                </Label>
                <Select 
                  value={formData.level}
                  onValueChange={(value) => handleSelectChange("level", value)}
                >
                  <SelectTrigger id="level" className="mt-1">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="all">All Levels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="maxParticipants" className="text-right">
                  Max Participants
                </Label>
                <Input
                  id="maxParticipants"
                  name="maxParticipants"
                  type="number"
                  min="1"
                  value={formData.maxParticipants}
                  onChange={handleChange}
                  placeholder="e.g., 20"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="isRemote" 
                  checked={formData.isRemote}
                  onCheckedChange={(checked) => handleSwitchChange("isRemote", checked)}
                />
                <Label htmlFor="isRemote" className="cursor-pointer">Remote/Online</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="isCertified" 
                  checked={formData.isCertified}
                  onCheckedChange={(checked) => handleSwitchChange("isCertified", checked)}
                />
                <Label htmlFor="isCertified" className="cursor-pointer">Offers Certificate</Label>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="sm:order-1 order-2">
              Cancel
            </Button>
            <Button type="submit" className="sm:order-2 order-1 w-full sm:w-auto">
              Publish Training Program
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
